import {
  ApiPvpApplicationsUserApplicationsCreateError,
  useApiAllUserApplicationsList,
  useApiPvpApplicationsList,
  useApiUserApplicationsCreate,
} from "@/api/lctApIComponents";
import { StatusEnum, UserApplicationCreate } from "@/api/lctApISchemas";
import { Button } from "@/components/ui/button";
import { useAppSelector } from "@/hooks/reduxHooks";
import { useNavigate } from "@tanstack/react-router";
import { StatusObject } from "../StatusPill";
import { CircleCheck, Clock, Info, Pencil, TriangleAlert, FileText } from "lucide-react";
import { ApplicationStatus } from "@/utils/constants";
import { useDates } from "@/hooks/useDates";
import { ApplicationCreationDialog } from "@/pvp/components/ApplicationCreationDialog";
import { useState } from "react";

interface IProps {
  title?: string;
  programId: string;
  description?: string;
  updatedAt?: string;
  isApplicable?: boolean;
  lastUpdated?: string;
  onViewApplications?: (pvpId: string) => void;
}

// Button variants used by ShadUI
export type ButtonVariant = "link" | "outline" | "default" | "disabled" | "constructive" | "destructive" | "secondary" | "ghost";

interface StatusDetails extends StatusObject {
  buttonText: string;
  buttonVariant: ButtonVariant;
}

const statusDetails = new Map<StatusEnum, StatusDetails>([
  [
    ApplicationStatus.STARTED,
    {
      bgColor: "bg-blue-100",
      textColor: "text-blue-900",
      icon: Pencil,
      text: "In Progress",
      buttonText: "Continue",
      buttonVariant: "default",
    },
  ],
  [
    ApplicationStatus.PART_1_SUBMITTED,
    {
      bgColor: "bg-yellow-100",
      textColor: "text-yellow-900",
      icon: Clock,
      text: "Part 1 Awaiting Review",
      buttonText: "View",
      buttonVariant: "secondary",
    },
  ],
  [
    ApplicationStatus.PART_1_APPROVED,
    {
      bgColor: "bg-primaryAccent",
      textColor: "text-green-900",
      icon: CircleCheck,
      text: "Part 1 Approved",
      buttonText: "View",
      buttonVariant: "default",
    },
  ],
  [
    ApplicationStatus.PART_2_SUBMITTED,
    {
      bgColor: "bg-yellow-100",
      textColor: "text-yellow-900",
      icon: Info,
      text: "Part 2 Awaiting Review",
      buttonText: "View",
      buttonVariant: "secondary",
    },
  ],
  [
    ApplicationStatus.APPROVED,
    {
      bgColor: "bg-primaryAccent",
      textColor: "text-green-900",
      icon: CircleCheck,
      text: "Approved",
      buttonText: "View",
      buttonVariant: "default",
    },
  ],
  [
    ApplicationStatus.REJECTED,
    {
      bgColor: "bg-red-50",
      textColor: "text-error",
      icon: TriangleAlert,
      text: "Rejected",
      buttonText: "Review",
      buttonVariant: "secondary",
    },
  ],
  [
    ApplicationStatus.FURTHER_INFO_PART_1,
    {
      bgColor: "bg-yellow-100",
      textColor: "text-yellow-900",
      icon: Info,
      text: "Review Feedback for Part 1",
      buttonText: "Review and Edit",
      buttonVariant: "outline",
    },
  ],
  [
    ApplicationStatus.FURTHER_INFO_PART_2,
    {
      bgColor: "bg-yellow-100",
      textColor: "text-yellow-900",
      icon: Info,
      text: "Review Feedback for Part 2",
      buttonText: "Review and Edit",
      buttonVariant: "outline",
    },
  ],
]);

const defaultStatus: StatusDetails = {
  bgColor: "bg-gray-100",
  textColor: "text-gray-900",
  icon: Info,
  text: "Application has 2 parts",
  buttonText: "Apply",
  buttonVariant: "default",
};

export const ProgramTile: React.FC<IProps> = ({ title, programId, description, isApplicable, lastUpdated, onViewApplications }) => {
  const navigate = useNavigate();
  const token = useAppSelector((state) => state.user.authKey);
  const { formatDateToShortDate } = useDates();
  const [showCreationDialog, setShowCreationDialog] = useState(false);

  const { data, isLoading } = useApiPvpApplicationsList(
    { headers: { Authorization: `Token ${token}` }, pathParams: { pvp: programId } },
    { staleTime: 1000, enabled: !!programId },
  );

  const appId = data?.results.find((app) => app.active)?.id ?? "";
  const appName = data?.results.find((app) => app.active)?.title ?? "";

  const createUserApplication = useApiUserApplicationsCreate();

  const { data: allUserApplications } = useApiAllUserApplicationsList(
    {
      headers: { Authorization: `Token ${token}` },
    },
    {
      enabled: !!appId,
    },
  );
  const userApplication = allUserApplications?.results.find((userApp) => userApp.application.id === appId);
  const appStatus = userApplication?.status;

  const handleCreationSubmit = (name: string) => {
    const body: Omit<UserApplicationCreate, "id"> = { application: appId, name };
    createUserApplication.mutate(
      {
        headers: { Authorization: `Token ${token}` },
        body: body as UserApplicationCreate,
      },
      {
        onSuccess: (response) => {
          if (response) {
            setShowCreationDialog(false);
            navigate({
              to: "/pvp/$pvpId",
              params: { pvpId: programId ?? "" },
              search: { userAppId: response.id, appId: appId },
            });
          } else {
            console.error("ID missing in the response, unexpected server response:", response);
          }
        },
        onError: (error: ApiPvpApplicationsUserApplicationsCreateError) => {
          console.error("Error creating user application:", error);
        },
      },
    );
  };

  const handleClick = () => {
    if (isApplicable && appId) {
      setShowCreationDialog(true);
    }
  };

  const statusDetail = appStatus ? (statusDetails.get(appStatus) ?? defaultStatus) : defaultStatus;

  return programId ? (
    <section className="prose relative flex min-w-60 max-w-[98%] flex-1 flex-col justify-between space-y-4 rounded-lg border border-slate-300 border-opacity-90 bg-white p-6 align-middle">
      <div className="flex flex-col space-y-2">
        <span className="text-lg font-medium text-black">{title}</span>
        <span className="pt-1 text-sm font-normal text-slate-600">{description}</span>
        <span className="text-xs font-normal text-muted-foreground">{`Last updated: ${formatDateToShortDate(lastUpdated ?? "")}`}</span>
      </div>
      <div className="flex flex-wrap justify-end gap-2">
        {userApplication ? (
          <Button variant="outline" onClick={() => onViewApplications?.(programId)}>
            <FileText className="mr-2 h-4 w-4" />
            View my applications
          </Button>
        ) : null}
        {isApplicable && appId ? (
          <Button loading={isLoading} onClick={handleClick} variant={statusDetail.buttonVariant}>
            Start new application
          </Button>
        ) : null}
      </div>
      <ApplicationCreationDialog
        open={showCreationDialog}
        onOpenChange={setShowCreationDialog}
        onSubmit={handleCreationSubmit}
        title={`Begin a new application for a group of candidate cattle under the ${appName}`}
        description={"This will generate a separate application and won't impact any of your applications currently in progress."}
        isLoading={createUserApplication.isPending}
      />
    </section>
  ) : null;
};
